import { addMonths, isBefore, isWithinInterval, startOfDay, startOfMonth, subDays } from 'app/lib/date';
import { useDeleteMyProfileUnavailability, useMyProfile, useUpdateMyProfileUnavailability } from 'app/network/profile';
import { type SetStateAction, useState } from 'react';

function parseApiTimestampAsLocalDay(isoString: string): Date {
  return startOfDay(new Date(isoString));
}

export function useUnavailableScreen({
  onSaveSuccess,
  onDeleteSuccess,
}: {
  onSaveSuccess: () => void;
  onDeleteSuccess: () => void;
}) {
  const { data: profile, isPending: isLoadingProfile } = useMyProfile();

  const updateUnavailability = useUpdateMyProfileUnavailability({ onSuccess: onSaveSuccess });
  const deleteUnavailability = useDeleteMyProfileUnavailability({ onSuccess: onDeleteSuccess });

  const today = startOfDay(new Date());
  const currentMonthStart = startOfMonth(today);
  const maxSelectableMonthStart = addMonths(currentMonthStart, 14);

  const isUnavailableDay = (date: Date) => {
    const day = startOfDay(date);
    const dayBeforeToday = subDays(today, 1);
    const endBeforeCurrent = subDays(currentMonthStart, 1);
    if (isWithinInterval(day, { start: addMonths(currentMonthStart, -12), end: endBeforeCurrent })) {
      return true;
    }
    if (
      !isBefore(dayBeforeToday, currentMonthStart) &&
      isWithinInterval(day, { start: currentMonthStart, end: dayBeforeToday })
    ) {
      return true;
    }
    return false;
  };

  const existingUnavailability = profile?.unavailability?.[0] ?? null;
  const existingSuspendNotifications = profile?.suspendNotificationsDuringUnavailability ?? false;
  const profileSourceKey = profile
    ? `${existingUnavailability?.start ?? ''}|${existingUnavailability?.end ?? ''}|${String(existingSuspendNotifications)}`
    : 'loading';
  const profileDraft = {
    sourceKey: profileSourceKey,
    selectedStartDate: existingUnavailability ? parseApiTimestampAsLocalDay(existingUnavailability.start) : null,
    selectedEndDate: existingUnavailability ? parseApiTimestampAsLocalDay(existingUnavailability.end) : null,
    suspendNotificationsDuringUnavailability: existingUnavailability ? existingSuspendNotifications : false,
    willNowChoose: 'start' as 'start' | 'end',
  };
  const [editedDraft, setEditedDraft] = useState(profileDraft);
  let activeDraft = editedDraft;
  if (editedDraft.sourceKey !== profileSourceKey) {
    activeDraft = profileDraft;
    setEditedDraft(activeDraft);
  }
  const { selectedStartDate, selectedEndDate, suspendNotificationsDuringUnavailability, willNowChoose } = activeDraft;

  const updateDraft = (update: (currentDraft: typeof activeDraft) => typeof activeDraft) => {
    setEditedDraft((currentDraft) => update(currentDraft.sourceKey === profileSourceKey ? currentDraft : profileDraft));
  };
  const setSelectedStartDate = (selectedDate: Date | null) => {
    updateDraft((currentDraft) => ({ ...currentDraft, selectedStartDate: selectedDate }));
  };
  const setSelectedEndDate = (selectedDate: Date | null) => {
    updateDraft((currentDraft) => ({ ...currentDraft, selectedEndDate: selectedDate }));
  };
  const setSuspendNotificationsDuringUnavailability = (suspendNotifications: SetStateAction<boolean>) => {
    updateDraft((currentDraft) => ({
      ...currentDraft,
      suspendNotificationsDuringUnavailability:
        typeof suspendNotifications === 'function'
          ? suspendNotifications(currentDraft.suspendNotificationsDuringUnavailability)
          : suspendNotifications,
    }));
  };
  const setWillNowChoose = (nextSelection: 'start' | 'end') => {
    updateDraft((currentDraft) => ({ ...currentDraft, willNowChoose: nextSelection }));
  };

  const onDatePress = (date: Date) => {
    const day = startOfDay(date);
    if (isBefore(day, today)) {
      return;
    }

    if (!selectedStartDate || selectedEndDate) {
      setSelectedStartDate(day);
      setSelectedEndDate(null);
      setWillNowChoose('end');
      return;
    }

    if (isBefore(day, selectedStartDate)) {
      setSelectedEndDate(selectedStartDate);
      setSelectedStartDate(day);
      setWillNowChoose('start');
      return;
    }

    setSelectedEndDate(day);
    setWillNowChoose('start');
  };

  const saveForm = async () => {
    if (!selectedStartDate || !selectedEndDate) {
      return;
    }
    const start = startOfDay(selectedStartDate);
    const end = new Date(selectedEndDate);
    end.setHours(23, 59, 59, 999);
    if (isBefore(end, new Date())) {
      return;
    }
    await updateUnavailability.mutateAsync({
      unavailability: [{ start: start.toISOString(), end: end.toISOString() }],
      suspendNotificationsDuringUnavailability,
    });
  };

  const clearForm = async () => {
    await deleteUnavailability.mutateAsync();
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSuspendNotificationsDuringUnavailability(false);
    setWillNowChoose('start');
  };

  return {
    today,
    currentMonthStart,
    maxSelectableMonthStart,
    isUnavailableDay,
    selectedStartDate,
    selectedEndDate,
    suspendNotificationsDuringUnavailability,
    setSuspendNotificationsDuringUnavailability,
    /** Omitted while both dates are set so the calendar does not look like a new start date is being chosen. */
    willNowChoose: selectedStartDate && selectedEndDate ? undefined : willNowChoose,
    onDatePress,
    saveForm,
    clearForm,
    isSaving: updateUnavailability.isPending,
    isDeleting: deleteUnavailability.isPending,
    isLoadingProfile,
    hasPersistedUnavailability: Boolean(existingUnavailability),
    canSave: Boolean(selectedStartDate && selectedEndDate),
  };
}
